import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, inr, px, sign } from "@/lib/api";

// Live Zerodha check shown inside the "app restarted" warning: intraday (MIS) positions + pending orders
export function KiteBook({ testId = "kite-book", onClean }) {
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => api.get("/kite/open_book").then((r) => setB(r.data)).catch((e) => setB({ ok: false, error: errMsg(e) })), []);
  useEffect(() => { load(); const i = setInterval(load, 15000); return () => clearInterval(i); }, [load]);
  // nothing left open at Zerodha → the warning is no longer needed
  useEffect(() => { if (onClean && b?.ok && !b.positions.length && !b.orders.length) onClean(); }, [b, onClean]);
  const close = async (p) => {
    const msg = p.qty ? `Square off ${p.sym} ${Math.abs(p.qty)} (${p.qty > 0 ? "SELL" : "BUY"}) at market now? Its pending orders will be cancelled.` : `Cancel all pending ${p.sym} orders?`;
    if (!window.confirm(msg)) return;
    setBusy(p.sym);
    try { const r = await api.post("/kite/square_off", { sym: p.sym }); setB(r.data.book); toast.success(r.data.notes.join(" · ")); }
    catch (e) { toast.error(errMsg(e)); load(); }
    setBusy(null);
  };
  if (!b) return <p className="text-xs text-slate-400 mt-2" data-testid={testId}>Checking your Zerodha account…</p>;
  if (!b.ok) return <p className="text-xs text-amber-300 mt-2" data-testid={testId}>Couldn't check Zerodha: {b.error}. Open Kite and check manually.</p>;
  const clean = !b.positions.length && !b.orders.length;
  return (
    <div className="mt-3 rounded-md border border-[#1E293B] bg-[#090D16]/70 p-3 text-xs" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <span className="label-xs">Zerodha now (intraday)</span>
        <span className="num text-slate-500">checked {b.checked_at}</span>
      </div>
      {clean ? (
        <p className="text-cyan-300 font-semibold" data-testid={`${testId}-clean`}>No open intraday positions and no pending orders — nothing is left unmanaged.</p>
      ) : (
        <div className="space-y-2">
          {b.positions.map((p) => (
            <div key={p.sym} className="flex flex-wrap items-center gap-x-3 gap-y-1 num" data-testid={`${testId}-pos-${p.sym}`}>
              <span className="text-red-300 font-bold">OPEN</span><span className="text-slate-100 font-semibold">{p.sym}</span>
              <span>{p.qty > 0 ? "LONG" : "SHORT"} {Math.abs(p.qty)} @ {px(p.avg)}</span><span>LTP {px(p.ltp)}</span>
              <span className={sign(p.pnl)}>{inr(p.pnl)}</span>
              <button onClick={() => close(p)} disabled={busy === p.sym} data-testid={`${testId}-squareoff-${p.sym}`}
                className="ml-auto h-8 px-3 rounded-md bg-red-500 hover:bg-red-400 text-white font-semibold disabled:opacity-50">{busy === p.sym ? "Closing…" : "Square off"}</button>
            </div>
          ))}
          {b.orders.map((o) => (
            <div key={o.id} className="flex flex-wrap gap-x-3 num text-slate-300" data-testid={`${testId}-order-${o.id}`}>
              <span className="text-amber-300 font-semibold">PENDING</span><span className="text-slate-100">{o.sym}</span>
              <span>{o.side} {o.qty} {o.type}</span>{!!o.trigger && <span>trig {px(o.trigger)}</span>}{!!o.price && <span>@ {px(o.price)}</span>}
              <span className="text-slate-500">{o.status}{o.app ? " · placed by app" : ""}</span>
              {!b.positions.some((p) => p.sym === o.sym) && (
                <button onClick={() => close({ sym: o.sym, qty: 0 })} disabled={busy === o.sym} data-testid={`${testId}-cancel-${o.id}`}
                  className="ml-auto h-7 px-3 rounded-md border border-amber-500/60 text-amber-300 font-semibold disabled:opacity-50">{busy === o.sym ? "Cancelling…" : "Cancel"}</button>
              )}
            </div>
          ))}
        </div>
      )}
      {!!b.other_positions && <p className="text-slate-500 mt-2">{b.other_positions} non-intraday (CNC/MTF/NRML) position{b.other_positions > 1 ? "s" : ""} not shown — the app only trades intraday MIS.</p>}
    </div>
  );
}
